import { User } from './types';
import { getLangKey } from '../../../clients/sidebar/models/Language';
import * as translations from './strings';
import { MentionableTask } from '../../../clients/sidebar/views/CreateTask/create_task/types';
const strings = translations[getLangKey()];
import cx from 'classnames';
import styles from './index.module.css';
import { mentionRegex } from '.';

const localTaskIdRegex = (id: string) =>
  new RegExp(`@\\[([##\\d^\\[]+)\\]\\((${id})\\)`);

const isAnId = (id: string): boolean =>
  !id.match(/\D+/g)?.length && !isNaN(parseInt(id, 10));

const getUser = (id: string, users: User[]): User | undefined =>
  users.find((user: User) => user.id === parseInt(id, 10));

const getTask = (id: string, tasks: MentionableTask[]) =>
  tasks.find((task: MentionableTask) => task.id === parseInt(id, 10));

const getUserMentionTitle = ({
  id,
  projectUser,
  user
}: {
  id: string;
  projectUser?: User;
  user?: User;
}): string => {
  if (projectUser) {
    return `${projectUser.name}: ${projectUser.email}`;
  } else if (user) {
    return strings.noAccess(user.name);
  } else if (isAnId(id)) {
    return strings.noFoundUser;
  }
  return id;
};

const mentionReplacedDescription = (description?: string): string =>
  (description || '').replace(
    mentionRegex,
    (match, p1) => `${p1.slice(0, 2) === '##' ? p1 : `@${p1}`}`
  );

const getTaskMentionTitle = ({
  id,
  task
}: {
  id: string;
  task?: MentionableTask;
}): string => {
  if (task) return mentionReplacedDescription(task.display);
  else if (isAnId(id)) return strings.noFoundTask;
  return id;
};

const escapeRegexSensitiveCharacters = (str: string) =>
  str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getRemovedUserName = (id: string, description: string) => {
  const escapedId = escapeRegexSensitiveCharacters(id);
  const regexString = `@\\[([^\\[]+)\\]\\(${escapedId}\\)`;
  const idSpecificRegex = new RegExp(regexString, 'g');
  const matches = description.match(idSpecificRegex);
  const match =
    matches &&
    matches.find(
      (match: string) => match.indexOf(id) === match.length - 1 - id.length
    );
  return match?.replace(idSpecificRegex, '$1');
};

const getUserMentionClassName = ({
  projectUser,
  user,
  isComment,
  isPrivate,
  id
}: {
  projectUser?: User;
  user?: User;
  isComment?: boolean;
  isPrivate?: boolean;
  id: string;
}) =>
  cx({
    [styles.mention]: projectUser,
    [styles.notMention]: !projectUser,
    [styles.isPrivateComment]: !projectUser && isComment && isPrivate,
    [styles.isCommentMention]: projectUser && isComment,
    [styles.notOnProject]: !projectUser && user,
    [styles.noUserFound]: !user && isAnId(id)
  });

const isTaskMention = (description: string, id: string) => {
  const escapedId = escapeRegexSensitiveCharacters(id);
  return description.match(localTaskIdRegex(escapedId));
};

export {
  getUser,
  getTask,
  getUserMentionTitle,
  getTaskMentionTitle,
  getRemovedUserName,
  getUserMentionClassName,
  isTaskMention,
  localTaskIdRegex,
  mentionReplacedDescription,
  escapeRegexSensitiveCharacters
};
